// authServiceUtil.js
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, getCredentialCheckAuth } from '../firebase';
import { ADMIN_IDLE_TIMEOUT_MS } from '../constants/bookingConst';

const DEFAULT_EMAIL = 'system@gmail.com';
const DEFAULT_PASSWORD = 'websitepal@2026';

/**
 * Normalizes an email for comparison (trimmed, lower-cased). Firebase
 * echoes back whatever casing the account was created with, so raw
 * equality checks against a stored value are unreliable.
 * @param {string} email
 * @returns {string}
 */
const normalizeEmail = (email) => (email || '').trim().toLowerCase();

/**
 * True for the shared account every visitor is silently signed in as at
 * boot (see initializeDefaultUser).
 *
 * NOTE: this is NOT a test for "nobody is logged in as an admin". The same
 * account can also be a real admin login (it's the super-admin in
 * CONFIG_ADMIN_EMAILS), so admin-ness is tracked by the explicit session
 * marker below instead. This is only used to decide whether a leftover
 * Firebase login needs to be swapped back to the shared account.
 * @param {string} email
 * @returns {boolean}
 */
export const isSystemAccount = (email) => normalizeEmail(email) === normalizeEmail(DEFAULT_EMAIL);

/**
 * The email of whoever is signed in to Firebase right now, or '' if nobody
 * is.
 *
 * Read at call time rather than threaded down as a prop, for the same reason
 * resolveAdminActor in auditLogUtil.js is: otherwise every screen that needs
 * to know who is acting has to receive it from AdminPage through components
 * that have no other use for it.
 *
 * @returns {string}
 */
export const currentAdminEmail = () => auth?.currentUser?.email || '';

/**
 * Whether two emails refer to the same account, case/whitespace-insensitively.
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export const isSameAccount = (a, b) => {
  const left = normalizeEmail(a);
  return !!left && left === normalizeEmail(b);
};

// ---- Admin idle session ----
// Firebase keeps an admin signed in indefinitely once they authenticate,
// which is wrong for a shared back-office machine. So the dashboard tracks
// its own session record — which account logged in, and when they last
// interacted — independently of Firebase's persistence: a refresh resumes
// the session, an idle tab doesn't.
//
// The account has to be stored alongside the timestamp because the Firebase
// user alone can't answer "did an admin log in here?" — every visitor is
// silently signed in as the shared system account, and that same account is
// also a valid admin login. Storing the email is what lets both cases be
// told apart (and is why refreshing used to bounce the super-admin, but not
// other admins, back to the login form).
const ADMIN_SESSION_KEY = 'jt-admin-session';
// Superseded by ADMIN_SESSION_KEY; removed on write/clear so old records
// can't linger in a browser that's already been used with the previous build.
const LEGACY_ADMIN_ACTIVITY_KEY = 'jt-admin-last-active';
// ADMIN_IDLE_TIMEOUT_MS now lives in constants/bookingConst.js — the login
// card and the Config -> System description both quote it, so it belongs
// with the other shared values rather than here.

/**
 * The stores an admin session can live in, persistent one first.
 * localStorage is "Keep me signed in"; sessionStorage survives a refresh but
 * ends with the tab. Wrapped, because touching either throws in some
 * private-browsing modes.
 * @returns {Storage[]}
 */
const adminSessionStores = () => {
  const stores = [];
  try { if (window.localStorage) stores.push(window.localStorage); } catch { /* unavailable */ }
  try { if (window.sessionStorage) stores.push(window.sessionStorage); } catch { /* unavailable */ }
  return stores;
};

/**
 * Reads the raw stored record, and the store holding it, without applying
 * the idle timeout.
 * @returns {{session: {email: string, lastActive: number}, store: Storage}|null}
 */
const readAdminEntry = () => {
  for (const store of adminSessionStores()) {
    try {
      const raw = store.getItem(ADMIN_SESSION_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const email = normalizeEmail(parsed?.email);
      const lastActive = Number(parsed?.lastActive) || 0;
      if (email && lastActive) return { session: { email, lastActive }, store };
    } catch {
      // Storage unavailable (private mode) or a malformed record — treated
      // as "no session", which is the safe failure for an admin screen.
    }
  }
  return null;
};

/**
 * Reads the raw stored record without applying the idle timeout.
 * @returns {{email: string, lastActive: number}|null}
 */
const readAdminSession = () => readAdminEntry()?.session || null;

/**
 * Persists the record. Silently gives up when storage is unavailable; the
 * session then just won't survive a refresh.
 * @param {{email: string, lastActive: number}} session
 * @param {Storage} [store] - where to keep it; localStorage by default
 */
const writeAdminSession = (session, store) => {
  try {
    (store || window.localStorage).setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
    window.localStorage.removeItem(LEGACY_ADMIN_ACTIVITY_KEY);
  } catch {
    // Ignored — see readAdminEntry.
  }
};

/**
 * Opens an admin session for the given account. Called once, on a
 * successful login; starts the idle window.
 * @param {string} email Account that just authenticated.
 * @param {boolean} [remember=true] "Keep me signed in": false ends the
 *   session when the tab closes (a refresh still keeps it).
 */
export const startAdminSession = (email, remember = true) => {
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  // Cleared first, so a record left in the other store by an earlier login
  // cannot outlive the choice made this time.
  clearAdminActivity();
  let store;
  try {
    store = remember ? window.localStorage : window.sessionStorage;
  } catch {
    return;
  }
  writeAdminSession({ email: normalized, lastActive: Date.now() }, store);
};

/**
 * Records "the admin is still here". No-op when there's no session to
 * extend, so background activity can't resurrect an expired one.
 */
export const touchAdminActivity = () => {
  const entry = readAdminEntry();
  if (!entry) return;
  // Back into the store it came from, so a tab-only session stays tab-only.
  writeAdminSession({ ...entry.session, lastActive: Date.now() }, entry.store);
};

/** Forgets the session entirely, so the next load requires a fresh login. */
export function clearAdminActivity() {
  adminSessionStores().forEach((store) => {
    try {
      store.removeItem(ADMIN_SESSION_KEY);
      store.removeItem(LEGACY_ADMIN_ACTIVITY_KEY);
    } catch {
      // Ignored, same reason as above.
    }
  });
}

/**
 * Whether the admin has been idle past the timeout. Treats a missing
 * record as expired, so a Firebase session left over from before this
 * feature existed can't silently grant access.
 *
 * The idle window itself is optional (Config -> System): a club running the
 * dashboard on its own front-desk machine can turn it off and stay signed in
 * until someone logs out by hand. Turning it off never removes the session
 * RECORD — only the expiry check — so re-enabling it starts the clock from
 * the admin's last real interaction rather than logging them straight out.
 *
 * Defaults to enabled: a caller that forgets to pass the setting gets the
 * safe behaviour, not an admin session that never ends.
 *
 * @param {boolean} [idleTimeoutEnabled=true]
 * @returns {boolean}
 */
export const isAdminSessionExpired = (idleTimeoutEnabled = true) => {
  const session = readAdminSession();
  if (!session) return true;
  if (!idleTimeoutEnabled) return false;
  return (Date.now() - session.lastActive) > ADMIN_IDLE_TIMEOUT_MS;
};

/**
 * The admin session to resume, or null when there isn't a live one. Use
 * this rather than inspecting the Firebase user: it's the only thing that
 * distinguishes a deliberate admin login from the app's shared background
 * session, and it applies the idle timeout.
 * @param {boolean} [idleTimeoutEnabled=true] See isAdminSessionExpired.
 * @returns {{email: string, lastActive: number}|null}
 */
export const getAdminSession = (idleTimeoutEnabled = true) => {
  const session = readAdminSession();
  if (!session) return null;
  return isAdminSessionExpired(idleTimeoutEnabled) ? null : session;
};

/**
 * How long to wait for Firebase to report on a persisted session before
 * giving up and signing in from scratch.
 *
 * Firebase resolves persistence by reading IndexedDB, and that read can stall
 * indefinitely rather than fail: a version-change request blocked by another
 * open tab of the same site, private-browsing modes that stub the store out,
 * tracking prevention, or a corrupted database. The observer below simply
 * never fires in those cases, and its error callback is only for auth errors,
 * so without a deadline the whole app boot hangs on it.
 *
 * Short on purpose: falling through costs one sign-in request, while waiting
 * costs the guest the entire page.
 */
const RESTORED_SESSION_TIMEOUT_MS = 3000;

/**
 * Resolves with the session Firebase restored from local persistence, or
 * null if there isn't one. Firebase reads persisted sessions asynchronously,
 * so `auth.currentUser` is still null for a moment right after boot —
 * checking it directly would miss an existing session and sign in again.
 *
 * Never rejects and never hangs: anything other than a prompt answer resolves
 * null, which the caller reads as "no session to reuse" and handles by
 * signing in normally.
 *
 * @returns {Promise<import('firebase/auth').User|null>}
 */
const getRestoredSession = () => new Promise((resolve) => {
  let settled = false;
  let unsubscribe = null;
  let timer = null;

  const finish = (value) => {
    if (settled) return;
    settled = true;
    if (timer) clearTimeout(timer);
    // Null-checked rather than called straight from inside its own
    // initialiser: onAuthStateChanged may invoke the observer synchronously,
    // before it has returned anything to assign here.
    if (unsubscribe) unsubscribe();
    resolve(value);
  };

  timer = setTimeout(() => finish(null), RESTORED_SESSION_TIMEOUT_MS);
  unsubscribe = onAuthStateChanged(auth, (user) => finish(user), () => finish(null));

  // Covers that synchronous case: the observer already ran and finish() had no
  // unsubscribe to call, so the subscription is torn down here instead.
  if (settled && unsubscribe) unsubscribe();
});

/**
 * Ensures there's a signed-in Firebase user before the app renders (see
 * AuthGuard.jsx), since the Realtime Database rules gate reads/writes on
 * `auth != null`.
 *
 * Order matters here. This used to call createUserWithEmailAndPassword
 * FIRST and rely on the resulting `auth/email-already-in-use` failure to
 * fall through to sign-in — so every page load fired a request that 400'd
 * in the console, then a second one to actually sign in, with the boot
 * spinner blocked on both. Now it reuses a persisted session when there is
 * one (no network call at all), signs in when there isn't, and only
 * attempts to create the account if it genuinely doesn't exist yet.
 *
 * @returns {Promise<import('firebase/auth').User>}
 */
export const initializeDefaultUser = async () => {
  const restored = await getRestoredSession();
  if (restored) return restored;

  try {
    const userCredential = await signInWithEmailAndPassword(auth, DEFAULT_EMAIL, DEFAULT_PASSWORD);
    return userCredential.user;
  } catch (error) {
    // Only a genuinely missing account should trigger creation. Newer
    // Firebase versions report a bad password as 'auth/invalid-credential'
    // rather than 'auth/wrong-password', and creating on that would just
    // fail again with email-already-in-use — so let it surface instead.
    if (error.code === 'auth/user-not-found') {
      const userCredential = await createUserWithEmailAndPassword(auth, DEFAULT_EMAIL, DEFAULT_PASSWORD);
      return userCredential.user;
    }
    throw error;
  }
};

/**
 * Checks an email and password WITHOUT signing this browser in as them.
 *
 * The whole point is the absence of a side effect. A normal
 * signInWithEmailAndPassword on the primary auth instance replaces the
 * signed-in user for every tab on this origin — which is why an administrator
 * lost their session the moment a till signed in, and why two tills could not
 * be open at once. This runs on a separate, in-memory-only instance and signs
 * out again immediately, so nothing outside this call is disturbed.
 *
 * Errors are passed through unchanged, so callers keep reporting
 * auth/invalid-credential and friends exactly as they did before.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<string>} the address Firebase confirmed, canonically cased
 * @throws {Error} the Firebase auth error, with its `code` intact
 */
export const verifyCredentials = async (email, password) => {
  const checkAuth = getCredentialCheckAuth();
  if (!checkAuth) throw new Error('Firebase is not configured.');

  try {
    const credential = await signInWithEmailAndPassword(checkAuth, email, password);
    return credential.user?.email || email;
  } finally {
    // Always, including after a failure: a verified session left open on this
    // instance would be a second signed-in user nobody is tracking.
    await signOut(checkAuth).catch(() => {});
  }
};
